import { action, observable } from "mobx";
import type { RequestErrorModel } from "models/error/Error";
import { logsRepository } from "repositories";
import { useEventQueue } from "./useEventQueue";

interface LogsStore {
  error: RequestErrorModel | null;
  setError: (error: RequestErrorModel) => void;
  trackTestEvent: (
    eventName: string,
    eventType: string,
    testToken: string,
    extraData?: Record<string, unknown>,
    timestamp?: Date,
  ) => void;
  eventQueue: {
    enqueueEvent: (event: {
      eventName: string;
      eventType: string;
      testToken: string;
      timestamp: Date;
      extraData?: Record<string, unknown>;
    }) => void;
  } | null;
}

interface ErrorResponse {
  status: number;
  message: string;
  type: string;
}

const initialState = {
  error: null,
  eventQueue: null,
};

const stateSetters = {
  setError: action((error: RequestErrorModel) => {
    store.error = error;
  }),
};

const apiRequests = {
  trackTestEvent: action(
    (
      eventName: string,
      eventType: string,
      testToken: string,
      extraData?: Record<string, unknown>,
    ) => {
      const currentDate = new Date();
      if (store.eventQueue) {
        store.eventQueue.enqueueEvent({
          eventName,
          eventType,
          testToken,
          timestamp: currentDate,
          extraData,
        });
      } else {
        logsRepository
          .trackTestEvent(eventName, eventType, testToken, currentDate, extraData)
          .catch((e) => {
            const { message, status, type } = e as ErrorResponse;
            store.setError({ message, status, type });
          });
      }
    },
  ),
};

const store: LogsStore = observable({
  ...initialState,
  ...stateSetters,
  ...apiRequests,
} as LogsStore);

export const useLogs = (): LogsStore => {
  const { enqueueEvent, setTrackEventFunction } = useEventQueue();

  setTrackEventFunction(async (event) => {
    try {
      await logsRepository.trackTestEvent(
        event.eventName,
        event.eventType,
        event.testToken,
        event.timestamp,
        event.extraData,
      );
    } catch (e) {
      const { message, status, type } = e as ErrorResponse;
      store.setError({ message, status, type });
    }
  });

  store.eventQueue = { enqueueEvent };

  return store;
};
